import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Radio,
    Paper,
    TextField,
    CircularProgress
} from "@mui/material";
import {
    Close as CloseIcon,
    Description as FormIcon,
    Warning as MandatoryIcon,
    Schedule as OptionalIcon
} from "@mui/icons-material";
import { t as coachT } from "../dashboard/coach/coachTokens";

const SendFormToClientDialog = ({ open, onClose, onSend, client, availableForms = [], loading = false, coach = false }) => {
    const [selectedFormId, setSelectedFormId] = useState("");
    const [isMandatory, setIsMandatory] = useState(true);
    const [formName, setFormName] = useState("");

    useEffect(() => {
        if (open) {
            setSelectedFormId("");
            setIsMandatory(true);
            setFormName("");
        }
    }, [open]);

    // Update form name when selected form changes
    useEffect(() => {
        const selectedForm = availableForms.find(f => f.id === selectedFormId);
        if (selectedForm) {
            setFormName(selectedForm.title || "");
        }
    }, [selectedFormId, availableForms]);

    const handleSend = () => {
        if (!selectedFormId) return;

        const selectedForm = availableForms.find(f => f.id === selectedFormId);
        if (onSend) {
            onSend(selectedForm, isMandatory, formName);
        }
        onClose();
    };

    const selectedForm = availableForms.find(f => f.id === selectedFormId);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: coach ? coachT.r : "12px",
                    direction: "rtl"
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "20px 24px",
                backgroundColor: coach ? coachT.bg : undefined,
                borderBottom: coach ? `1px solid ${coachT.border}` : "1px solid #E2E8F0"
            }}>
                <Box>
                    <Typography sx={{
                        fontSize: "18px",
                        fontWeight: coach ? 800 : 600,
                        color: coach ? coachT.text : "#1E293B",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        שליחת טופס למתאמן
                    </Typography>
                    <Typography sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        marginTop: "4px",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}>
                        שלח טופס ל{client?.name}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ padding: "24px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Step 1: Select Form */}
                    <Box>
                        <Typography sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1E293B",
                            marginBottom: "12px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}>
                            בחר טופס לשליחה
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: "240px",
                                overflow: "auto",
                                borderRadius: "8px",
                                border: "1px solid #E2E8F0"
                            }}
                        >
                            {loading ? (
                                <Box sx={{
                                    padding: "32px",
                                    textAlign: "center",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}>
                                    <CircularProgress sx={{ color: "#57837E" }} size={32} />
                                </Box>
                            ) : availableForms.length > 0 ? (
                                availableForms.map((form) => (
                                    <Box
                                        key={form.id}
                                        onClick={() => setSelectedFormId(form.id)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            padding: "12px 16px",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #F1F5F9",
                                            backgroundColor: selectedFormId === form.id ? "rgba(87, 131, 126, 0.08)" : "transparent",
                                            borderRight: selectedFormId === form.id ? "3px solid #57837E" : "3px solid transparent",
                                            transition: "all 0.2s",
                                            "&:hover": {
                                                backgroundColor: "rgba(87, 131, 126, 0.04)"
                                            },
                                            "&:last-child": {
                                                borderBottom: "none"
                                            }
                                        }}
                                    >
                                        <Radio
                                            checked={selectedFormId === form.id}
                                            sx={{
                                                color: "#CBD5E1",
                                                "&.Mui-checked": {
                                                    color: "#57837E"
                                                }
                                            }}
                                        />
                                        <FormIcon sx={{ color: "#57837E", fontSize: "20px" }} />
                                        <Typography sx={{
                                            fontSize: "14px",
                                            color: "#1E293B",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}>
                                            {form.title}
                                        </Typography>
                                    </Box>
                                ))
                            ) : (
                                <Box sx={{
                                    padding: "32px",
                                    textAlign: "center"
                                }}>
                                    <Typography sx={{
                                        fontSize: "14px",
                                        color: "#64748B",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}>
                                        אין טפסים זמינים לשליחה
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {/* Step 2: Form Name */}
                    {selectedFormId && (
                        <Box>
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                שם הטופס
                            </Typography>
                            <TextField
                                fullWidth
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                placeholder="הזן שם לטופס..."
                                size="small"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px"
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {/* Step 3: Mandatory or Optional */}
                    {selectedFormId && (
                        <Box sx={{ paddingTop: "16px", borderTop: "1px solid #E2E8F0" }}>
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1E293B",
                                marginBottom: "16px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                האם השאלון הוא חובה למילוי מיידי או אופציונלי למילוי בהמשך?
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {/* Mandatory Option */}
                                <Paper
                                    variant="outlined"
                                    onClick={() => setIsMandatory(true)}
                                    sx={{
                                        padding: "16px",
                                        cursor: "pointer",
                                        borderRadius: "8px",
                                        border: isMandatory ? "2px solid #57837E" : "1px solid #E2E8F0",
                                        backgroundColor: isMandatory ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            borderColor: "#57837E"
                                        }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                        <Radio
                                            checked={isMandatory}
                                            sx={{
                                                padding: 0,
                                                color: "#CBD5E1",
                                                "&.Mui-checked": {
                                                    color: "#57837E"
                                                }
                                            }}
                                        />
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: "4px" }}>
                                                <MandatoryIcon sx={{ color: "#DC2626", fontSize: "18px" }} />
                                                <Typography sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    color: "#1E293B",
                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                }}>
                                                    חובה למילוי מיידי
                                                </Typography>
                                            </Box>
                                            <Typography sx={{
                                                fontSize: "13px",
                                                color: "#64748B",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            }}>
                                                השאלון יסומן כחובה עבור המתאמן ויופיע עם תווית "חובה למילוי" באפליקציה
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>

                                {/* Optional Option */}
                                <Paper
                                    variant="outlined"
                                    onClick={() => setIsMandatory(false)}
                                    sx={{
                                        padding: "16px",
                                        cursor: "pointer",
                                        borderRadius: "8px",
                                        border: !isMandatory ? "2px solid #57837E" : "1px solid #E2E8F0",
                                        backgroundColor: !isMandatory ? "rgba(87, 131, 126, 0.04)" : "transparent",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            borderColor: "#57837E"
                                        }
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                        <Radio
                                            checked={!isMandatory}
                                            sx={{
                                                padding: 0,
                                                color: "#CBD5E1",
                                                "&.Mui-checked": {
                                                    color: "#57837E"
                                                }
                                            }}
                                        />
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: "4px" }}>
                                                <OptionalIcon sx={{ color: "#3B82F6", fontSize: "18px" }} />
                                                <Typography sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    color: "#1E293B",
                                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                                }}>
                                                    אופציונלי למילוי בהמשך
                                                </Typography>
                                            </Box>
                                            <Typography sx={{
                                                fontSize: "13px",
                                                color: "#64748B",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            }}>
                                                השאלון יהיה זמין למתאמן למילוי בכל עת, ללא דרישה למילוי מיידי
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    )}

                    {/* Summary */}
                    {selectedFormId && (
                        <Paper
                            sx={{
                                padding: "16px",
                                backgroundColor: "#F8FAFC",
                                borderRadius: "8px",
                                border: "1px solid #E2E8F0"
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                <FormIcon sx={{ color: "#57837E", fontSize: "20px", marginTop: "2px" }} />
                                <Box>
                                    <Typography sx={{
                                        fontSize: "13px",
                                        color: "#64748B",
                                        marginBottom: "4px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}>
                                        סיכום שליחה:
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#1E293B",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}>
                                        {selectedForm?.title}
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: "13px",
                                        color: "#64748B",
                                        marginTop: "4px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }}>
                                        יישלח ל{client?.name} כ
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontWeight: 600,
                                                color: isMandatory ? "#DC2626" : "#3B82F6"
                                            }}
                                        >
                                            {isMandatory ? "חובה למילוי מיידי" : "אופציונלי"}
                                        </Typography>
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{
                padding: "16px 24px",
                borderTop: "1px solid #E2E8F0",
                gap: 2
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        borderColor: "#E2E8F0",
                        color: "#64748B",
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        "&:hover": {
                            borderColor: "#CBD5E1",
                            backgroundColor: "#F8FAFC"
                        }
                    }}
                >
                    ביטול
                </Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    disabled={!selectedFormId}
                    sx={{
                        flex: 1,
                        backgroundColor: coach ? coachT.green : "#57837E",
                        borderRadius: coach ? coachT.rs : undefined,
                        boxShadow: coach ? "none" : undefined,
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        "&:hover": {
                            backgroundColor: coach ? coachT.greenDark : "#46706B",
                            boxShadow: coach ? "none" : undefined
                        },
                        "&:disabled": {
                            backgroundColor: "#E2E8F0",
                            color: "#94A3B8"
                        }
                    }}
                >
                    שלח טופס
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SendFormToClientDialog;
